import {ActionIcon, Paper, SegmentedControl} from "@mantine/core";
import {Checkbox} from "@mantine/core";
import {Modal} from "@mantine/core";
import {NumberInput} from "@mantine/core";
import {Button} from "@mantine/core";
import { InfoIcon, TrashIcon } from "@phosphor-icons/react";
import React, {useState, useMemo, useCallback, useEffect} from 'react';
import withRouteErrorBoundary from "../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import {Col, Row} from "../../components/Flex/Flex";
import Page from "../../components/Page/Page.tsx";
import AFImporter from "./AFImporter";
import style from './VisionTool.module.css';
import {v4 as uuidv4} from "uuid";
import {useParams} from "react-router";
import useSavedItem from "../../components/SharedSave/useSavedItem.ts";
import SharedSave from "../../components/SharedSave/SharedSave.tsx";


const modes: {label: string, value: TileMode}[] = [
    {label: 'None', value: 'read'},
    {label: 'Source', value: 'source'},
    {label: 'Obstacle', value: 'blocking'},
    {label: 'Asteroid', value: 'asteroid'},
]
const locations = [
    {label: 'Space', value: 'space'},
    {label: 'Ground', value: 'ground'}
]

export const OBSTACLE = {
    NONE: 0,
    ASTEROID: 1,
    BLOCKING: 2,
};

type FieldMap = number[][];

type TileMode = 'read'|'asteroid'|'blocking'|'source';

let initial: FieldMap = [];
for (let y = 0; y <= 21; y++) {
    initial[y] = [];
    for (let x = 0; x <= 21; x++) {
        initial[y][x] = OBSTACLE.NONE;
    }
}


const logger = {
    active: false,
    buffer: [] as (string|number)[][],
    log: (...args: (string|number)[]) => {
        logger.buffer.push([...args]);
    },
    print: (name: string) => {
        if (logger.active) {
            console.groupCollapsed(name);
            for (let i = 0; i < logger.buffer.length; i++) {
                console.log(...logger.buffer[i]);
            }
            console.groupEnd();
        }
        logger.buffer = [];
    },
    clear: () => logger.buffer = []
};

const roundFromReference = (num: number, ref: number) => {
    let diff = num - ref;
    let off = diff < 0 ? Math.round(diff) : -Math.round(-diff);
    return ref + off;
}

const calculatePath = (startX: number, startY: number, targetX: number, targetY: number) => {
    let points = [];
    const startOffset = 1;
    if (startY === targetY) {
        //vertical
        if (startX < targetX) {
            // down
            for (let pX = startX + startOffset; pX <= targetX; pX++) {
                points.push([pX, targetY]);
            }
        } else {
            //up
            for (let pX = startX - startOffset; pX >= targetX; pX--) {
                points.push([pX, targetY]);
            }
        }
    } else if (startX === targetX) {
        // horizontal
        if (startY < targetY) {
            // right
            for (let pY = startY + startOffset; pY <= targetY; pY++) {
                points.push([targetX, pY]);
            }
        } else {
            // left
            for (let pY = startY - startOffset; pY >= targetY; pY--) {
                points.push([targetX, pY]);
            }
        }
    } else {
        // diagonals
        let m = (targetY - startY) / (targetX - startX);
        let q = targetY - m * targetX;

        if (Math.abs(m) > 1) {
            logger.log('|m| > 1');
            if (startY < targetY) {
                //logger.log('N');
                for (let pY = startY + startOffset; pY <= targetY; pY++) {
                    let pX = roundFromReference((pY - q) / m, startX);
                    //logger.log(pX, pY, (pY - q) / m, pY);
                    points.push([pX, pY]);
                }
            } else {
                //logger.log('S');
                for (let pY = startY - startOffset; pY >= targetY; pY--) {
                    let pX = roundFromReference((pY - q) / m, startY);
                    //logger.log(pX, pY, (pY - q) / m, pY);
                    points.push([pX, pY]);
                }
            }
        } else {
            if (startX < targetX) {
                //logger.log('E');
                for (let pX = startX + startOffset; pX <= targetX; pX++) {
                    let pY = roundFromReference(m * pX + q, startY);
                    //logger.log(pX, pY);
                    points.push([pX, pY]);
                }
            } else {
                //logger.log('W');
                for (let pX = startX - startOffset; pX >= targetX; pX--) {
                    let pY = roundFromReference(m * pX + q, startY);
                    //logger.log(pX, pY);
                    points.push([pX, pY]);
                }
            }
        }
    }
    return points;
}

const getPointSensors = (sourceX: number, sourceY: number, targetX: number, targetY: number, power: number) => {
    let distance = Math.ceil(Math.sqrt(Math.pow(sourceX - targetX, 2) + Math.pow(sourceY - targetY, 2)));
    let raw = power / ((1 + distance));
    return Math.floor(raw);
}

const decreasePower = (power: number) => {
    //return Math.floor(power/2);
    return power / 2;
}

const getInitialPower = (source: Source) => {
    if(source.type === 'perception') {
        let power = source.sensors + 4;
        if(source.macroBins) {
            power += 5;
        }
        return power;
    } else {
        return source.sensors + 1;
    }
}


interface Source {
    uid: string,
    type: 'perception' | 'sensors',
    sensors: number,
    macroBins: boolean,
    x: number,
    y: number,
}
interface CellResult {
    x: number,
    y: number,
    hasSource: boolean,
    obstacle: number,
    sensors: number
}

function VisionTool() {
    const {id} = useParams();
    const savedData = useSavedItem('vision', id);
    const [field, setField] = useState<FieldMap>(initial);
    const [sources, setSources] = useState<Source[]>([]);
    const [mode, setMode] = useState<TileMode>('read');
    const [location, setLocation] = useState('space');
    const [showInfo, setShowInfo] = useState(false);

    const savedContent = savedData?.content;

    useEffect(() => {
        if (!savedContent) {
            return;
        }
        setField(savedContent.field ?? initial);
        setSources(savedContent.sources ?? []);
        setLocation(savedContent.location ?? 'space');
    }, [savedData?.id, savedContent]);

    const currentData = useMemo(() => ({
        field,
        sources,
        location
    }), [field, sources, location]);

    const draw = useMemo(() => {
        //console.log('draw', isBlocking);
        const MAX = location==='ground' ? 21 : 19;
        const SENSOR_DIFF = 1;
        let temp: CellResult[][] = [];
        for (let y = 0; y <= MAX; y++) {
            temp[y] = [];
            for (let x = 0; x <= MAX; x++) {
                let hasSource = sources.find(el => el.x === x && el.y === y);

                temp[y][x] = {x, y, hasSource: !!hasSource, obstacle: field[y] ? field[y][x] : OBSTACLE.NONE, sensors: 0};
            }
        }

        sources.forEach(s => {
            let range = getInitialPower(s) + SENSOR_DIFF;

            let x1 = s.x - range;
            let x2 = s.x + range;
            let y1 = s.y - range;
            let y2 = s.y + range;

            temp[s.y][s.x].sensors += getInitialPower(s);


            logger.log('source', s.x, s.y, x1, x2, y1, y2);

            let inner: FieldMap = [];
            for (let x = x1; x <= x2; x++) {
                let path1 = calculatePath(s.x, s.y, x, y1);
                let path2 = calculatePath(s.x, s.y, x, y2);

                let power = getInitialPower(s);
                for (let i = 0; i < path1.length; i++) {
                    let [x, y] = path1[i];
                    if (x < 0 || x > MAX || y < 0 || y > MAX) {
                        continue;
                    }
                    let p = getPointSensors(s.x, s.y, x, y, power);
                    if (!inner[y]) {
                        inner[y] = [];
                    }
                    logger.log(x, y, inner[y][x] ?? null, p);
                    if (!inner[y][x]) {
                        inner[y][x] = p;
                        temp[y][x].sensors += p;
                    } else if (inner[y][x] !== p) {
                        inner[y][x] = Math.max(inner[y][x], p);
                        logger.log('conflict ', x, y, inner[y][x], p);
                    }
                    if (field[y][x] === OBSTACLE.BLOCKING) {
                        power = 0;
                    }
                    if (field[y][x] === OBSTACLE.ASTEROID) {
                        power = decreasePower(power);
                    }
                }

                power = getInitialPower(s)
                for (let i = 0; i < path2.length; i++) {
                    let [x, y] = path2[i];
                    if (x < 0 || x > MAX || y < 0 || y > MAX) {
                        continue;
                    }
                    let p = getPointSensors(s.x, s.y, x, y, power);
                    if (!inner[y]) {
                        inner[y] = [];
                    }
                    logger.log(x, y, inner[y][x] ?? null, p);
                    if (!inner[y][x]) {
                        inner[y][x] = p;
                        temp[y][x].sensors += p;
                    } else if (inner[y][x] !== p) {
                        inner[y][x] = Math.max(inner[y][x], p);
                        logger.log('conflict ', x, y, inner[y][x], p);
                    }

                    if (field[y][x] === OBSTACLE.BLOCKING) {
                        power = 0;
                    }
                    if (field[y][x] === OBSTACLE.ASTEROID) {
                        power = decreasePower(power);
                    }
                }
            }

            for (let y = y1; y <= y2; y++) {
                let path1 = calculatePath(s.x, s.y, x1, y);
                let path2 = calculatePath(s.x, s.y, x2, y);

                let power = getInitialPower(s);
                for (let i = 0; i < path1.length; i++) {
                    let [x, y] = path1[i];
                    if (x < 0 || x > MAX || y < 0 || y > MAX) {
                        continue;
                    }
                    let p = getPointSensors(s.x, s.y, x, y, power);
                    if (!inner[y]) {
                        inner[y] = [];
                    }
                    logger.log(x, y, inner[y][x] ?? null, p);

                    if (!inner[y][x]) {
                        inner[y][x] = p;
                        temp[y][x].sensors += p;
                    } else if (inner[y][x] !== p) {
                        inner[y][x] = Math.max(inner[y][x], p);
                        logger.log('conflict ', x, y, inner[y][x], p);
                    }

                    if (field[y][x] === OBSTACLE.BLOCKING) {
                        power = 0;
                    }
                    if (field[y][x] === OBSTACLE.ASTEROID) {
                        power = decreasePower(power);
                    }
                }

                power = getInitialPower(s)
                for (let i = 0; i < path2.length; i++) {
                    let [x, y] = path2[i];
                    if (x < 0 || x > MAX || y < 0 || y > MAX) {
                        continue;
                    }
                    let p = getPointSensors(s.x, s.y, x, y, power);
                    if (!inner[y]) {
                        inner[y] = [];
                    }
                    logger.log(x, y, inner[y][x] ?? null, p);

                    if (!inner[y][x]) {
                        inner[y][x] = p;
                        temp[y][x].sensors += p;
                    } else if (inner[y][x] !== p) {
                        inner[y][x] = Math.max(inner[y][x], p);
                        logger.log('conflict ', x, y, inner[y][x], p);
                    }
                    if (field[y][x] === OBSTACLE.BLOCKING) {
                        power = 0;
                    }
                    if (field[y][x] === OBSTACLE.ASTEROID) {
                        power = decreasePower(power);
                    }
                }
            }

            logger.print(s.x + ' ' + s.y);
        });


        return temp;
    }, [field, sources, location, logger.active]);

    const [countBlocking, countAsteroids] = useMemo(() => {
        return [
            field.reduce((tot, row) => tot + row.reduce((acc, cell) => acc + (cell === OBSTACLE.BLOCKING ? 1 : 0), 0), 0),
            field.reduce((tot, row) => tot + row.reduce((acc, cell) => acc + (cell === OBSTACLE.ASTEROID ? 1 : 0), 0), 0),
        ]
    }, [field]);
    const coverage = useMemo(() => {
        return draw.reduce((tot, row) => tot + row.reduce((acc, cell) => acc + (cell.sensors ? 1 : 0), 0), 0);
    }, [draw]);


    const toggleElement = useCallback((x: number, y: number, mode: TileMode, prevObstacle: number) => {
        if (mode === 'asteroid' || mode === 'blocking') {
            setField(prev => {
                let f = [...prev];
                f[y] = [...f[y]];
                if (prevObstacle !== OBSTACLE.NONE) {
                    f[y][x] = OBSTACLE.NONE;
                } else if (mode === 'asteroid') {
                    f[y][x] = OBSTACLE.ASTEROID
                } else if (mode === 'blocking') {
                    f[y][x] = OBSTACLE.BLOCKING;
                }
                return f;
            })
        }
        if (mode === 'source') {
            setSources(prev => [...prev, {uid: uuidv4(), x, y, type:'sensors', macroBins: false, sensors: 1}]);
        }
    }, []);

    const updateSource = useCallback((uid: string, field: keyof Source, value: string|number|boolean) => {
        setSources(prev => {
            let next = [...prev];
            let i = next.findIndex(s => s.uid === uid);
            next[i] = {...next[i]};
            // @ts-expect-error
            next[i][field] = value;
            return next;
        })
    }, []);

    const removeSource = useCallback((uid: string) => {
        setSources(p => p.filter(el => el.uid !== uid));
    }, []);

    return (
        <Page>
            <title>Vision Simulator</title>
            <Row>
                <Col grow={1}>
                    <h2>Vision Simulator</h2>
                </Col>
                <Col>
                    <SharedSave type="vision" content={currentData} savedData={savedData} itemPath="/vision"/>
                </Col>
                <Col>
                    <Button leftSection={<InfoIcon />} onClick={() => setShowInfo(true)}>Important Info</Button>
                </Col>
            </Row>
            <Paper shadow="xs" p="md" withBorder>
                <div className={style.wrapper}>
                    <div className={style.map}>
                        <div className={style.row}>
                            <CellHead/>
                            <CellHead label={0}/>
                            <CellHead label={1}/>
                            <CellHead label={2}/>
                            <CellHead label={3}/>
                            <CellHead label={4}/>
                            <CellHead label={5}/>
                            <CellHead label={6}/>
                            <CellHead label={7}/>
                            <CellHead label={8}/>
                            <CellHead label={9}/>
                            <CellHead label={10}/>
                            <CellHead label={11}/>
                            <CellHead label={12}/>
                            <CellHead label={13}/>
                            <CellHead label={14}/>
                            <CellHead label={15}/>
                            <CellHead label={16}/>
                            <CellHead label={17}/>
                            <CellHead label={18}/>
                            <CellHead label={19}/>
                            {location === 'ground' ? (<>
                                <CellHead label={20}/>
                                <CellHead label={21}/>
                            </>) : null}
                        </div>
                        {draw.map((list, i) => (
                            <div key={i} className={style.row}>
                                <CellHead label={i}/>
                                {list.map((el, j) => <FieldCell key={j} cell={el}
                                                                mode={mode}
                                                                toggleElement={toggleElement}/>)}
                            </div>)
                        )}
                    </div>
                    <div className={style.side}>
                        <h4 style={{marginTop: 0}}>Location</h4>
                        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                            <SegmentedControl data={locations} onChange={val => setLocation(val)} value={location} />
                            <AFImporter onParse={setField} />
                        </div>

                        <h4>Add Element</h4>
                        <SegmentedControl data={modes} onChange={(val: TileMode) => setMode(val)} value={mode}/>
                        <hr/>
                        Total Obstacles: {countBlocking}<br/>
                        Total Asteroids: {countAsteroids}<br/>
                        Coverage: {coverage}/400 ({coverage / 4}%)<br/>
                        <hr/>
                        {sources.map(s => <Source key={s.uid} source={s} removeSource={removeSource} updateSource={updateSource}/>)}

                        {/*<h4>Export/Import</h4>*/}
                        {/*<InputTextarea value={impExp} onChange={e => setImpExp(e.target.value)}/><br/>*/}
                        {/*<div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>*/}
                        {/*    <Button onClick={() => setImpExp(JSON.stringify(field))} label="Export"/>*/}
                        {/*    <Button onClick={() => {*/}
                        {/*        let imp = JSON.parse(impExp);*/}
                        {/*        if (imp) {*/}
                        {/*            setField(imp);*/}
                        {/*        }*/}
                        {/*    }} label="Import" style={{margin: 6}}/>*/}
                        {/*    <Button onClick={() => setImpExp('')} label="Reset"/>*/}
                        {/*</div>*/}
                    </div>
                </div>
                <Modal style={{maxWidth: '50vw'}} opened={showInfo} onClose={() => setShowInfo(false)} title="Important Info">
                    <p className="text-red">WARNING: this tool has not been tested extensively. If you notice a discrepancy please report it on the discord server or in private with as many details as possible so it can be reproduced.</p>

                    <h4>How to add and remove elements</h4>
                    <p>
                    Select what you want to add (source, obstacles or asteroid) and then click on the map to place it.<br/>
                    Obstacles and asteroids will be toggled if clicked again, while multiple sources can be added at the same coordinates.<br />
                    </p>
                    <hr />
                    <h4>Managing sources</h4>
                    <p>
                        You can change the sensor power/perception used in the sidebar.<br/>
                        By default sources behave like Ships, Vehicles and Space Stations. Selecting "Use perception" will switch the source to behave like Character, NPCs, Droids and Creatures, using the number as the perception skill.<br />
                        If using as perception the "Use Macrobins" option will be available, adding 5 power to the source, as per the rules.
                    </p>
                    <hr />
                    <h4>Obstacles</h4>
                    <p>
                        Obstacles are blocking obstacles, like planets or facilities.<br />
                        Asteroids will instead behave like asteroid fields and decrease the sensor power.
                    </p>
                </Modal>
            </Paper>
        </Page>
    )

}


const CellHead = ({label}: {label?: string|number}) => {
    return <div className={style.cellHead}>{label}</div>;
}

const FieldCell = React.memo(function FieldCellRaw({cell, mode, toggleElement}: {
    cell: CellResult,
    mode: TileMode,
    toggleElement: (x: number, y: number, mode: TileMode, prevObstacle: number) => void,
}) {
    let s = style.cell;
    if (cell.obstacle === OBSTACLE.ASTEROID) {
        s += ' ' + style.asteroid;
    }
    if (cell.obstacle === OBSTACLE.BLOCKING) {
        s += ' ' + style.blocking;
    }
    if (mode !== 'read') {
        s += ' ' + style.clickable;
    }
    if (cell.sensors === 0) {
        s += ' ' + style.fog;
    }
    if (cell.hasSource) {
        s += ' ' + style.source;
    }
    return (
        <div className={s} onClick={mode !== 'read' ? () => toggleElement(cell.x, cell.y, mode, cell.obstacle) : undefined}>{cell.sensors}</div>
    );
});


const Source = ({source, updateSource, removeSource}: {source: Source, updateSource: (uid: string, field: keyof Source, value: string|number|boolean) => void, removeSource: (uid: string) => void}) => {

    return (
        <Row align="center">
            <Col grow={1}>({source.x}, {source.y})</Col>
            <Col>
                <Checkbox id={`source-type-${source.uid}`} label="Use perception" checked={source.type==='perception'} onChange={e => updateSource(source.uid, 'type', e.currentTarget.checked ? 'perception' : 'sensors')} />
                <Checkbox id={`source-macrobins-${source.uid}`} label="Use Macro Binoculars" checked={source.macroBins} onChange={e => updateSource(source.uid, 'macroBins', e.currentTarget.checked)} disabled={source.type !== 'perception'} />
            </Col>
            <Col><NumberInput value={source.sensors} onChange={val => updateSource(source.uid, 'sensors', val)} style={{width: 70}} min={0} max={source.type==='perception' ? 5 : 30}/></Col>
            <Col><ActionIcon variant="subtle" onClick={() => removeSource(source.uid)} title="Remove"><TrashIcon /></ActionIcon></Col>
        </Row>
    )
}


export default withRouteErrorBoundary(VisionTool);
